/**
 * The single entry point (ADR 0005): one `index.html`, one router, one navigate. React Router
 * in data mode, TanStack Query over the typed client (ADR 0007).
 *
 * The student area is `/`, the teacher area `/lehrer` (step 3 of the build spec). Each area
 * has its own root layout, which is where `data-theme` is stamped and the themes stay apart.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { reportClientError } from "./shared/api.js";
import { ApiClientError } from "./shared/errors.js";
import { StudentLayout } from "./student/StudentLayout.js";
import { StudentHome } from "./student/StudentHome.js";
import { LoginScreen } from "./student/LoginScreen.js";
import { CreditsScreen, PrivacyScreen, ProfileScreen } from "./student/ProfileScreen.js";
import { TeacherLayout } from "./teacher/TeacherLayout.js";
import { TeacherLoginScreen, TeacherSetupScreen } from "./teacher/TeacherAuthScreens.js";
import { ClassesScreen } from "./teacher/ClassesScreen.js";
import { ClassScreen } from "./teacher/ClassScreen.js";
import { StripsScreen } from "./teacher/StripsScreen.js";
import { StudentScreen } from "./teacher/StudentScreen.js";
import { TopicsScreen } from "./teacher/TopicsScreen.js";
import { TopicScreen } from "./teacher/TopicScreen.js";
import { FlagsScreen } from "./teacher/FlagsScreen.js";
import "./index.css";

/** Build spec 4.4: GETs retry through a cold start, mutations never do (they are not idempotent). */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        failureCount < 3 && !(error instanceof ApiClientError && error.status === 401),
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: { retry: false },
  },
});

export const router = createBrowserRouter([
  // The teacher routes come first: `/lehrer` must not be swallowed by the student area.
  { path: "/lehrer/login", element: <TeacherLoginScreen /> },
  { path: "/lehrer/setup/:token", element: <TeacherSetupScreen /> },
  {
    element: <TeacherLayout />,
    children: [
      { path: "/lehrer", element: <ClassesScreen /> },
      { path: "/lehrer/klassen/:id", element: <ClassScreen /> },
      { path: "/lehrer/klassen/:id/streifen", element: <StripsScreen /> },
      { path: "/lehrer/schueler/:id", element: <StudentScreen /> },
      { path: "/lehrer/themen", element: <TopicsScreen /> },
      { path: "/lehrer/themen/:slug", element: <TopicScreen /> },
      { path: "/lehrer/meldungen", element: <FlagsScreen /> },
    ],
  },
  {
    element: <StudentLayout />,
    children: [
      { path: "/", element: <StudentHome /> },
      { path: "/login", element: <LoginScreen /> },
      { path: "/profil", element: <ProfileScreen /> },
      { path: "/quellen", element: <CreditsScreen /> },
      { path: "/datenschutz", element: <PrivacyScreen /> },
    ],
  },
]);

// ADR 0011: first-party error reporting only; nothing leaves for a third party.
window.addEventListener("error", (event) => {
  reportClientError(event.message, event.error instanceof Error ? event.error.stack : undefined);
});
window.addEventListener("unhandledrejection", (event) => {
  const reason: unknown = event.reason;
  reportClientError(
    reason instanceof Error ? reason.message : String(reason),
    reason instanceof Error ? reason.stack : undefined,
  );
});

const container = document.getElementById("root");
if (container) {
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
